import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import {
    primaryText,
    secondaryText,
    greyTextColor,
    borderPrimary,
    placeholderPrimary,
    greenColorClass,
    cancelColorClass,
} from "../../../../../styling/stylingHelper";
import { cn } from "../../../../../lib/utils";
import { getMidLevelCategoryOptionGroups } from "../../../../orderhistory/categoryUtils";
import {
    CustomIngredient,
    createCustomIngredient,
    updateCustomIngredient,
    CustomIngredientDuplicateArticleApiError,
} from "../../../../../services/customIngredients/customIngredientsService";

interface CustomIngredientDetailModalProps {
    // null → create mode (empty form); an ingredient → edit it.
    ingredient: CustomIngredient | null;
    restaurantId: string;
    onClose: () => void;
    /** Receives the created/updated ingredient so callers can select it straight away. */
    onSaved: (ingredient: CustomIngredient) => void;
    /** Raise the modal above a slide-over it was opened from (default sits below them). */
    zIndexClass?: string;
}

const fieldInputBase = `rounded-lg border bg-backgroundSecondary dark:bg-backgroundSecondary-dark px-3 h-11 text-[14px] leading-5 ${secondaryText} ${placeholderPrimary} outline-none border-borderGrey dark:border-borderGrey-dark`;
const fieldInputClass = `w-full ${fieldInputBase}`;
const fieldLabelClass = `block mb-2 text-[14px] leading-5 font-medium ${primaryText}`;

const availableUnits = ["g", "ml", "p"];

/**
 * Slide-over side modal for creating/editing a custom ingredient. Same shell
 * pattern as SubrecipeDetailModal (portal to body, translate-x panel, maximize
 * toggle). This is a re-housing of the old centered Create/EditCustomIngredient
 * modals — the form fields, validation, VAT handling and payloads are unchanged.
 */
const CustomIngredientDetailModal: React.FC<CustomIngredientDetailModalProps> = ({ ingredient, restaurantId, onClose, onSaved, zIndexClass = "z-30" }) => {
    const { t } = useTranslation();
    const isEditMode = ingredient !== null;

    const [formData, setFormData] = useState({
        productName: ingredient?.productName ?? "",
        articleId: ingredient?.articleId ?? "",
        customPlatformName: ingredient?.customPlatformName ?? "",
        category: ingredient?.category ?? "",
        price: ingredient ? ingredient.price.toString() : "",
        stdAmount: ingredient ? ingredient.quantityInfo.stdAmount.toString() : "",
        stdUnit: ingredient?.quantityInfo.stdUnit === "piece" ? "p" : ingredient?.quantityInfo.stdUnit ?? "g",
    });
    const [vatRate, setVatRate] = useState<"9" | "21">(ingredient ? (ingredient.taxRate === 0.21 ? "21" : "9") : "9");

    const [isPanelVisible, setIsPanelVisible] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        setIsPanelVisible(true);
        return () => { document.body.style.overflow = ""; };
    }, []);

    const handleClose = () => {
        setIsPanelVisible(false);
        setTimeout(onClose, 200);
    };

    // Live "incl. VAT" preview under the ex-VAT price field.
    const rateDecimal = vatRate === "21" ? 0.21 : 0.09;
    const exVatNum = parseFloat(formData.price);
    const inclVatPreview = !isNaN(exVatNum) && exVatNum > 0
        ? Math.round(exVatNum * (1 + rateDecimal) * 100) / 100
        : null;

    // Numeric-guard price + amount (digits + single decimal point, or empty).
    const handleNumericInputChange = (field: "price" | "stdAmount", value: string) => {
        if (value === "" || /^[0-9]*\.?[0-9]*$/.test(value)) {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleSave = async () => {
        if (!formData.productName.trim() || !formData.articleId.trim() || !formData.price ||
            !formData.customPlatformName.trim() || !formData.stdAmount) {
            setError(t("costPrice.createCustomIngredient.allFieldsRequired"));
            return;
        }
        const price = parseFloat(formData.price);
        const stdAmount = parseFloat(formData.stdAmount);
        if (price <= 0 || stdAmount <= 0) {
            setError(t("costPrice.createCustomIngredient.positiveNumbers"));
            return;
        }

        setIsSaving(true);
        setError(null);
        const priceWithTaxes = Math.round(price * (1 + rateDecimal) * 10000) / 10000;
        const quantityInfo = { stdAmount, stdMultiplier: 1, stdUnit: formData.stdUnit };
        try {
            let saved: CustomIngredient;
            if (isEditMode && ingredient) {
                saved = await updateCustomIngredient({
                    id: ingredient.id,
                    articleId: formData.articleId.trim(),
                    productName: formData.productName.trim(),
                    price,
                    priceWithTaxes,
                    taxRate: rateDecimal,
                    customPlatformName: formData.customPlatformName.trim(),
                    category: formData.category || null,
                    quantityInfo,
                });
            } else {
                saved = await createCustomIngredient({
                    restaurantId,
                    articleId: formData.articleId.trim(),
                    productName: formData.productName.trim(),
                    price,
                    priceWithTaxes,
                    taxRate: rateDecimal,
                    platform: "unsupported",
                    customPlatformName: formData.customPlatformName.trim(),
                    category: formData.category || null,
                    quantityInfo,
                });
            }
            onSaved(saved);
            handleClose();
        } catch (err) {
            console.error("Failed to save custom ingredient:", err);
            if (err instanceof CustomIngredientDuplicateArticleApiError) {
                setError(t("costPrice.createCustomIngredient.duplicateArticleError"));
            } else {
                // Prefer the server's message (parseCustomIngredientError surfaces it)
                // over the generic fallback so the user sees something actionable.
                const serverMessage = err instanceof Error ? err.message : null;
                setError(serverMessage || (isEditMode
                    ? t("costPrice.editCustomIngredient.updateFailed")
                    : t("costPrice.createCustomIngredient.createFailed")));
            }
            setIsSaving(false);
        }
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSave();
    };

    const title = isEditMode ? t("costPrice.editCustomIngredient.title") : t("costPrice.createCustomIngredient.title");

    // Portal to body: matches SubrecipeDetailModal so a `transform` ancestor
    // (e.g. the cost-price panel this can be opened from) can't confine the
    // fixed slide-over.
    return createPortal(
        <div
            className={cn("fixed inset-0 flex justify-end bg-[#111827]/40 dark:bg-[#111827]/40 pt-[72px] md:pt-0", zIndexClass)}
            onClick={handleClose}
        >
            <div
                className={`h-full w-full bg-white dark:bg-background-dark shadow-xl flex flex-col transform transition-all duration-250 ease-out ${isPanelVisible ? "translate-x-0" : "translate-x-full"
                    } ${isMaximized ? "max-w-full md:max-w-[1024px]" : "max-w-full md:max-w-[584px]"}`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Desktop header: maximize (left), close (right) */}
                <div className="hidden md:flex items-center justify-between px-5 py-4 border-b border-[#E8E8E9] dark:border-borderPrimary-dark">
                    <button
                        type="button"
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                        onClick={() => setIsMaximized(prev => !prev)}
                    >
                        <img src="/assets/maximize.svg" alt="expand" />
                    </button>
                    <button
                        type="button"
                        onClick={handleClose}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    >
                        <img src="/assets/close-modal.svg" alt="close" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                <div className="flex-1 overflow-y-auto px-4 sm:px-5 pb-5 pt-5 md:pt-4">
                    {/* Mobile header */}
                    <div className="flex items-center gap-4 md:hidden mb-5">
                        <button type="button" onClick={handleClose}>
                            <img src="/assets/arrow-narrow-left.svg" alt="arrow-narrow-left" />
                        </button>
                        <span className="text-[24px] leading-9 font-semibold tracking-[-0.24px] text-[#181624] dark:text-textPrimary-dark">
                            {title}
                        </span>
                    </div>

                    <h2 className="hidden md:block text-[28px] leading-10 tracking-[-0.28px] font-bold text-[#161924] dark:text-textPrimary-dark mb-6">
                        {title}
                    </h2>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-[14px]">
                            {error}
                        </div>
                    )}

                    {/* Product name */}
                    <div className="mb-4">
                        <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.productName")}</label>
                        <input
                            type="text"
                            required
                            value={formData.productName}
                            onChange={(e) => setFormData(prev => ({ ...prev, productName: e.target.value }))}
                            placeholder={t("costPrice.createCustomIngredient.productNamePlaceholder")}
                            className={fieldInputClass}
                        />
                    </div>

                    {/* Article ID + Supplier name — side by side, wraps to stack
                        when the panel is too narrow (non-maximized / mobile). */}
                    <div className="flex flex-wrap gap-4 mb-4">
                        <div className="flex-1 min-w-[220px]">
                            <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.articleId")}</label>
                            <input
                                type="text"
                                required
                                value={formData.articleId}
                                onChange={(e) => setFormData(prev => ({ ...prev, articleId: e.target.value }))}
                                placeholder={t("costPrice.createCustomIngredient.articleIdPlaceholder")}
                                className={fieldInputClass}
                            />
                        </div>
                        <div className="flex-1 min-w-[220px]">
                            <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.platformName")}</label>
                            <input
                                type="text"
                                required
                                value={formData.customPlatformName}
                                onChange={(e) => setFormData(prev => ({ ...prev, customPlatformName: e.target.value }))}
                                placeholder={t("costPrice.createCustomIngredient.platformNamePlaceholder")}
                                className={fieldInputClass}
                            />
                        </div>
                    </div>

                    {/* Category */}
                    <div className="mb-4">
                        <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.category")}</label>
                        <select
                            value={formData.category}
                            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                            className={fieldInputClass}
                        >
                            <option value="">{t("costPrice.createCustomIngredient.noCategory")}</option>
                            {getMidLevelCategoryOptionGroups().map(group => (
                                <optgroup key={group.label} label={group.label}>
                                    {group.options.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                    </div>

                    {/* VAT rate */}
                    <div className="mb-4">
                        <label className={fieldLabelClass}>{t("costPrice.vatRate.label")}</label>
                        <div className="flex gap-2">
                            {(["9", "21"] as const).map(rate => (
                                <button
                                    key={rate}
                                    type="button"
                                    disabled={isSaving}
                                    onClick={() => setVatRate(rate)}
                                    className={cn(
                                        "flex-1 h-11 rounded-lg border text-[14px] font-semibold transition-colors",
                                        vatRate === rate
                                            ? "border-darkGreenColor dark:border-darkGreenColor-dark bg-[#F1FAF7] dark:bg-darkGreenColor/10 text-darkGreenColor dark:text-darkGreenColor-dark"
                                            : `border-borderGrey dark:border-borderGrey-dark ${secondaryText}`,
                                    )}
                                >
                                    {rate}%
                                </button>
                            ))}
                        </div>
                        <p className={`${greyTextColor} text-[12px] leading-4 mt-1`}>{t("costPrice.vatRate.helper")}</p>
                    </div>

                    {/* Price ex-VAT */}
                    <div className="mb-4">
                        <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.priceExVat")}</label>
                        <input
                            type="text"
                            inputMode="decimal"
                            required
                            value={formData.price}
                            onChange={(e) => handleNumericInputChange("price", e.target.value)}
                            className={fieldInputClass}
                        />
                        {inclVatPreview !== null && (
                            <p className={`${greyTextColor} text-[12px] leading-4 mt-1`}>
                                {t("costPrice.createCustomIngredient.inclVatPreview", { price: `€${inclVatPreview.toFixed(2)}`, rate: vatRate })}
                            </p>
                        )}
                    </div>

                    {/* Amount + unit */}
                    <div className="mb-2">
                        <label className={fieldLabelClass}>{t("costPrice.createCustomIngredient.stdAmount")}</label>
                        <div className="flex items-center gap-3">
                            <input
                                type="text"
                                inputMode="decimal"
                                required
                                value={formData.stdAmount}
                                onChange={(e) => handleNumericInputChange("stdAmount", e.target.value)}
                                className={`${fieldInputBase} flex-1 min-w-0`}
                            />
                            <select
                                value={formData.stdUnit}
                                onChange={(e) => setFormData(prev => ({ ...prev, stdUnit: e.target.value }))}
                                className={`${fieldInputBase} w-32 flex-shrink-0`}
                            >
                                {availableUnits.map(unit => (
                                    <option key={unit} value={unit}>
                                        {unit === "p" ? t("costPrice.addIngredient.piece") : unit}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                {/* Footer actions */}
                <div className={`flex items-center justify-between gap-4 sm:gap-6 px-4 sm:px-5 py-4 border-t bg-backgroundSecondary dark:bg-backgroundSecondary-dark ${borderPrimary}`}>
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={isSaving}
                        className={`flex-1 h-11 justify-center text-[14px] leading-5 font-semibold ${cancelColorClass}`}
                    >
                        {t("costPrice.create.cancel")}
                    </button>
                    <button
                        type="submit"
                        disabled={isSaving}
                        className={`flex-1 h-11 justify-center text-[16px] leading-6 font-semibold gap-2 ${greenColorClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                        {isSaving
                            ? (isEditMode ? t("common.saving") : t("costPrice.createCustomIngredient.creating"))
                            : (isEditMode ? t("common.save") : t("costPrice.createCustomIngredient.create"))}
                    </button>
                </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default CustomIngredientDetailModal;
